import { createActionHelpers } from 'vuex-loading';
import localForage from 'localforage';
import PromotionTypes from '../promotion-types';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const timeFormat = 'HH:mm:ss';
const dateFormat = 'YYYY-MM-DD';

function getDefaultStoreName() {
    return window.location.pathname;
}

function formatDateToString(date) {
    if (date === null) {
        return null;
    }

    if (date instanceof moment) {
        return date.format('YYYY-MM-DD[T]HH:mm:ssZZ');
    }

    return date;
}

function formatObject(obj) {
    Object.keys(obj).forEach(k => {
        if (obj[k] === '' || obj[k] === undefined) {
            obj[k] = null;
        }
    });

    return obj;
}

function formatStringToDate(date) {
    if (date === null) {
        return null;
    }

    return moment(date, dateFormat);
}

function mapToClientObject(p) {
    return {
        ...p,
        available_from: formatStringToDate(p.available_from),
        available_to: formatStringToDate(p.available_to),
        availability: p.availability.map(a => {
            return {
                days: a.days,
                from: moment(a.from, timeFormat),
                to: moment(a.to, timeFormat),
            };
        }),
        status: p.status == 1,
        type: PromotionTypes.find(t => t.value === p.type),
    };
}

function mapToServerObject(p) {
    let at = p.available_to;

    if (at !== null) {
        at = moment(at).endOf('day');
    }

    return {
        ...p,
        available_from: formatDateToString(p.available_from),
        available_to: formatDateToString(at),
        availability: p.availability.map(a => {
            return {
                days: a.days,
                from: a.from.second(0).format(timeFormat),
                to: a.to.second(59).format(timeFormat),
            };
        }),
        status: p.status ? 1 : 0,
        type: p.type ? p.type.value : null,
    };
}

const storage = localForage.createInstance({
    name: 'promotion',
});

const state = {
    calc: [],
    filterOutlets: [],
    filterTypes: [],
    promotionList: [],
    totalRecords: 0,
    promotion: {
        availability: [],
        available_from: null,
        available_to: null,
        name: null,
        trigger_conditions: [],
        apply_actions: [],
        outlets: [],
        status: true,
        type: null,
    },
    action_summary: {},
    trigger_summary: {},
};

const getters = {
    totalRecords: state => state.totalRecords,

    actionExists: state => {
        return action =>
            state.promotion.apply_actions.find(a => a.type === action) !==
            undefined;
    },

    triggerExists: state => {
        return trigger =>
            state.promotion.trigger_conditions.find(t => t.type === trigger) !==
            undefined;
    },

    filterOutlets: state => {
        return state.filterOutlets;
    },

    promotion: state => {
        return state.promotion;
    },

    promotionList: state => {
        return state.promotionList;
    },

    promotionEmpty: state => {
        return (
            state.promotion.trigger_conditions.length === 0 &&
            state.promotion.apply_actions.length === 0
        );
    },
};

const mutations = {
    addPromotionAction(state, action) {
        state.promotion.apply_actions = [
            ...state.promotion.apply_actions,
            action,
        ];
    },

    addPromotionTrigger(state, trigger) {
        state.promotion.trigger_conditions = [
            ...state.promotion.trigger_conditions,
            trigger,
        ];
    },

    removeActionSummary(state, index) {
        state.action_summary = {
            ...state.action_summary,
            [index]: null,
        };
    },

    removePromotionAction(state, index) {
        state.promotion.apply_actions.splice(index, 1);
    },

    removePromotionTrigger(state, index) {
        state.promotion.trigger_conditions.splice(index, 1);
    },

    removeTriggerSummary(state, index) {
        state.trigger_summary = {
            ...state.trigger_summary,
            [index]: null,
        };
    },

    setActionSummary(state, { index, summary }) {
        state.action_summary = {
            ...state.action_summary,
            [index]: summary,
        };
    },

    setFilterOutlets(state, outlets) {
        state.filterOutlets = outlets;
    },

    setFilterTypes(state, types) {
        state.filterTypes = types;
    },

    setPromotion(state, promotion) {
        state.promotion = promotion;
    },

    setPromotionAction(state, { index, action }) {
        state.promotion.apply_actions.splice(index, 1, formatObject(action));
    },

    setPromotionAvailability(state, availability) {
        state.promotion = {
            ...state.promotion,
            availability: [...availability],
        };
    },

    setPromotionAvailableFrom(state, date) {
        state.promotion = {
            ...state.promotion,
            available_from: date,
        };
    },

    setPromotionAvailableTo(state, date) {
        state.promotion = {
            ...state.promotion,
            available_to: date,
        };
    },

    setPromotionEnabled(state, enabled) {
        state.promotion.status = enabled;
    },

    setPromotionId(state, id) {
        state.promotion.id = id;
    },

    setPromotionName(state, name) {
        state.promotion.name = name;
    },

    setPromotionOutlets(state, outlets) {
        state.promotion.outlets = outlets;
    },

    setPromotionTrigger(state, { index, trigger }) {
        state.promotion.trigger_conditions.splice(
            index,
            1,
            formatObject(trigger),
        );
    },

    setPromotionType(state, type) {
        state.promotion.type = type;
    },

    setTriggerSummary(state, { index, summary }) {
        state.trigger_summary = {
            ...state.trigger_summary,
            [index]: summary,
        };
    },

    setPromotionList(state, promotionList) {
        state.promotionList = promotionList;
    },

    setTotalRecords(state, total) {
        state.totalRecords = total;
    },
};

const actions = {
    createPromotion({ commit, dispatch }, store = null) {
        if (store === null) {
            store = getDefaultStoreName();
        }

        startLoading(dispatch, 'save-promotion');

        let promotion = mapToServerObject(state.promotion);

        return axios
            .post(Laroute.route('franchise.promotion.store'), promotion)
            .then(res => {
                endLoading(dispatch, 'save-promotion');
                dispatch('resetLocalPromotion', store);
                return res;
            })
            .catch(err => {
                endLoading(dispatch, 'save-promotion');
                throw err;
            });
    },

    getLocalPromotion({ commit, dispatch }, store = null) {
        if (store === null) {
            store = getDefaultStoreName();
        }

        startLoading(dispatch, 'get-promotion');

        return storage.getItem(store).then(promotion => {
            if (promotion !== null) {
                state.promotion = mapToClientObject(promotion);
            }

            endLoading(dispatch, 'get-promotion');
        });
    },

    hasLocalPromotion({ commit, dispatch }, store = null) {
        if (store === null) {
            store = getDefaultStoreName();
        }

        startLoading(dispatch, 'get-promotion');

        return storage.getItem(store).then(promotion => {
            endLoading(dispatch, 'get-promotion');

            return promotion !== null;
        });
    },

    getPromotion({ commit, dispatch }, id) {
        startLoading(dispatch, 'get-promotion');

        return axios
            .get(Laroute.route('franchise.promotion.show', { id: id }))
            .then(res => {
                let data = res.data.data;

                commit(
                    'setPromotion',
                    mapToClientObject({
                        ...data,
                        outlets: data.outlets.map(o => o.id),
                    }),
                );
                endLoading(dispatch, 'get-promotion');

                return data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-promotion');
                throw err;
            });
    },

    resetLocalPromotion({ commit, dispatch }, store = null) {
        if (store === null) {
            store = getDefaultStoreName();
        }

        return storage.removeItem(store);
    },

    saveLocalPromotion({ commit, dispatch }, store = null) {
        let promotion = mapToServerObject(state.promotion);

        if (store === null) {
            store = getDefaultStoreName();
        }

        return storage.setItem(store, promotion);
    },

    updatePromotion({ commit, dispatch }, store) {
        if (store === null) {
            store = getDefaultStoreName();
        }

        startLoading(dispatch, 'save-promotion');

        let promotion = mapToServerObject(state.promotion);

        return axios
            .patch(
                Laroute.route('franchise.promotion.update', {
                    id: promotion.id,
                }),
                promotion,
            )
            .then(res => {
                endLoading(dispatch, 'save-promotion');
                dispatch('resetLocalPromotion', store);
                return res;
            })
            .catch(err => {
                endLoading(dispatch, 'save-promotion');
                throw err;
            });
    },

    async fetchPromotions(context, params = {}) {
        const url = Laroute.route('franchise.vue.promotions');
        const options = { params: Object.assign({}, params) };

        try {
            const { data } = await axios.get(url, options);
            return data;
        } catch (e) {
            throw e;
        }
    },

    async getPromotionList({ commit, dispatch }, params = {}) {
        try {
            startLoading(dispatch, 'load-promotion-list');
            const { data, count } = await dispatch('fetchPromotions', params);
            endLoading(dispatch, 'load-promotion-list');
            commit('setPromotionList', data);
            commit('setTotalRecords', count);
            return data;
        } catch (e) {
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/promotion.js